// Curated tag vocabulary plus editor-authored free-form tags.
//
// Curated tags provide stable labels and keyword matching. Editors may also add
// a small number of normalized custom tags; only tags attached to a post get an
// archive page, which avoids empty collection pages.
//
// Each tag carries keyword matchers (TR + EN) used to backfill and to keep
// newly published posts tagged even before an editor curates them.

export interface TagDef {
  /** URL slug — ASCII, stable, never renamed once published. */
  slug: string;
  tr: string;
  en: string;
  descTr: string;
  descEn: string;
  /** Lowercase, diacritic-folded keyword fragments. */
  match: string[];
}

export const TAGS: TagDef[] = [
  {
    slug: "yesil-aklama",
    tr: "Yeşil aklama",
    en: "Greenwashing",
    descTr:
      "Sürdürülebilirlik iddialarının pazarlamaya dönüşmesi, yeşil aklama ve ispat yükümlülüğü üzerine yazılar.",
    descEn: "Essays on greenwashing, sustainability claims and the burden of proof.",
    match: ["yesil aklama", "yesilaklama", "greenwash", "yesil badana"],
  },
  {
    slug: "veri-merkezleri",
    tr: "Veri merkezleri",
    en: "Data centers",
    descTr: "Yapay zekânın fiziksel altyapısı: veri merkezleri, çip üretimi, soğutma ve su.",
    descEn: "The physical infrastructure of AI: data centers, chips, cooling and water.",
    match: ["veri merkez", "data center", "data centre", "hyperscale", "sogutma", "gpu"],
  },
  {
    slug: "enerji",
    tr: "Enerji",
    en: "Energy",
    descTr: "Elektrik, fosil yakıtlar, yenilenebilir enerji ve enerji talebinin politikası.",
    descEn: "Electricity, fossil fuels, renewables and the politics of energy demand.",
    match: ["enerji", "elektrik", "energy", "komur", "coal", "petrol", "dogal gaz", "yenilenebilir", "renewable", "grid", "sebeke"],
  },
  {
    slug: "algoritmalar",
    tr: "Algoritmalar",
    en: "Algorithms",
    descTr: "Öneri sistemleri, algoritmik karar verme ve otomatikleşen yargı.",
    descEn: "Recommender systems, algorithmic decision-making and automated judgment.",
    match: ["algoritma", "algorithm", "oneri sistem", "recommend", "model egitim", "llm", "yapay zeka modeli"],
  },
  {
    slug: "yalnizlik",
    tr: "Yalnızlık",
    en: "Loneliness",
    descTr: "Bağlantı çağında yalnızlık, dijital yakınlık ve toplumsal kopuş.",
    descEn: "Loneliness in the age of connection, digital intimacy and social disconnection.",
    match: ["yalnizlik", "loneliness", "lonely", "izolasyon", "isolation", "yalniz"],
  },
  {
    slug: "iklim-politikasi",
    tr: "İklim politikası",
    en: "Climate policy",
    descTr: "COP süreçleri, iklim müzakereleri, ulusal katkılar ve düzenleme.",
    descEn: "COP processes, climate negotiations, national contributions and regulation.",
    match: ["cop3", "cop2", "iklim politik", "climate policy", "muzakere", "paris anlasma", "paris agreement", "ndc", "unfccc"],
  },
  {
    slug: "karbon-piyasalari",
    tr: "Karbon piyasaları",
    en: "Carbon markets",
    descTr: "Karbon fiyatlama, offset piyasaları, sınırda karbon düzenlemesi.",
    descEn: "Carbon pricing, offset markets and border carbon adjustment.",
    match: ["karbon piyasa", "karbon fiyat", "offset", "carbon market", "carbon credit", "cbam", "sinirda karbon", "emisyon ticaret"],
  },
  {
    slug: "yesil-finans",
    tr: "Yeşil finans",
    en: "Green finance",
    descTr: "Sürdürülebilir finans, ESG, yatırım akışları ve iklim riski.",
    descEn: "Sustainable finance, ESG, investment flows and climate risk.",
    match: ["yesil finans", "green finance", "esg", "surdurulebilir finans", "yatirim", "tahvil", "bond", "banka"],
  },
  {
    slug: "is-ve-beceri",
    tr: "İş ve beceri",
    en: "Work and skills",
    descTr: "Otomasyon, istihdam, beceri kaybı ve emeğin dönüşümü.",
    descEn: "Automation, employment, skill erosion and the transformation of labour.",
    match: ["istihdam", "isgucu", "employment", "workforce", "beceri", "skill", "otomasyon", "automation", "meslek", "job"],
  },
  {
    slug: "egitim",
    tr: "Eğitim",
    en: "Education",
    descTr: "Okul, üniversite ve öğrenmenin yapay zekâ çağındaki dönüşümü.",
    descEn: "Schools, universities and learning in the age of AI.",
    match: ["egitim", "education", "ogrenci", "student", "universite", "school", "okul", "ogretmen"],
  },
  {
    slug: "yonetisim",
    tr: "Yönetişim",
    en: "Governance",
    descTr: "Yapay zekâ düzenlemesi, hesap verebilirlik ve kurumsal güç.",
    descEn: "AI regulation, accountability and institutional power.",
    match: ["yonetisim", "governance", "duzenleme", "regulation", "ai act", "denetim", "hesap verebilir", "politika yapici"],
  },
  {
    slug: "kentler",
    tr: "Kentler",
    en: "Cities",
    descTr: "Kentsel yaşam, ulaşım, sıcak dalgaları ve kentlerin dayanıklılığı.",
    descEn: "Urban life, mobility, heatwaves and city resilience.",
    match: ["kent", "sehir", "urban", "city", "belediye", "metropol", "ulasim", "mobility"],
  },
  {
    slug: "su-ve-gida",
    tr: "Su ve gıda",
    en: "Water and food",
    descTr: "Su kıtlığı, tarım, gıda sistemleri ve kuraklık.",
    descEn: "Water scarcity, agriculture, food systems and drought.",
    match: ["su kitlig", "kuraklik", "drought", "water", "tarim", "agricultur", "gida", "food system", "hasat"],
  },
  {
    slug: "dijital-mahremiyet",
    tr: "Dijital mahremiyet",
    en: "Digital privacy",
    descTr: "Veri toplama, gözetim ve mahremiyetin sınırları.",
    descEn: "Data collection, surveillance and the limits of privacy.",
    match: ["mahremiyet", "privacy", "gozetim", "surveillance", "veri toplama", "kisisel veri", "personal data"],
  },
  {
    slug: "insan-zihni",
    tr: "İnsan zihni",
    en: "The human mind",
    descTr: "Dikkat, bellek, bilişsel yük ve teknolojinin zihinsel etkileri.",
    descEn: "Attention, memory, cognitive load and the mental effects of technology.",
    match: ["dikkat", "attention", "bilis", "cognit", "zihin", "beyin", "brain", "hafiza", "bellek", "memory", "aptallas"],
  },
];

export const TAG_SLUGS = TAGS.map((t) => t.slug);

const BY_SLUG = new Map(TAGS.map((t) => [t.slug, t]));

export function getTag(slug: string): TagDef | undefined {
  return BY_SLUG.get(slug);
}

/**
 * Turn a free-form label into a safe ASCII tag slug. Returns "" when nothing
 * usable is left, so callers can drop it.
 */
export function slugifyTag(label: string): string {
  return foldText(String(label))
    .trim()
    .replace(/\s+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 40);
}

/**
 * Keep curated slugs plus editor-authored free tags (normalised to safe ASCII
 * slugs). Anything that slugifies to nothing is dropped, and the list is capped
 * so tag archives stay meaningful.
 */
export function sanitizeTags(tags: readonly string[] | null | undefined): string[] {
  if (!tags) return [];
  const seen = new Set<string>();
  for (const t of tags) {
    const raw = String(t).trim().toLowerCase();
    const slug = BY_SLUG.has(raw) ? raw : slugifyTag(raw);
    if (slug.length >= 2 && !seen.has(slug)) seen.add(slug);
  }
  return [...seen];
}

/** Free-tag display fixes: slugs whose auto-capitalised label must be exact. */
const LABEL_OVERRIDES: Record<string, string> = {
  cop31: "COP31",
};

/** Display label for any tag slug — curated definition or humanised free tag. */
export function tagLabel(slug: string, lang: "tr" | "en" = "tr"): string {
  const override = LABEL_OVERRIDES[slug.toLowerCase()];
  if (override) return override;
  const def = BY_SLUG.get(slug);
  if (def) return lang === "tr" ? def.tr : def.en;
  const words = slug.replace(/-+/g, " ").trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
}


const TR_MAP: Record<string, string> = {
  ç: "c", Ç: "c", ğ: "g", Ğ: "g", ı: "i", İ: "i", ö: "o", Ö: "o",
  ş: "s", Ş: "s", ü: "u", Ü: "u", â: "a", Â: "a", î: "i", û: "u",
};

/** Lowercase, strip HTML and fold Turkish diacritics so matchers stay ASCII. */
export function foldText(input: string): string {
  return input
    .replace(/<[^>]*>/g, " ")
    .replace(/[çÇğĞıİöÖşŞüÜâÂîû]/g, (c) => TR_MAP[c] ?? c)
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, " ");
}

/**
 * Derive tags from post text using the curated vocabulary.
 * Capped at 4 tags per post (strongest matches first) so tag pages stay
 * meaningful instead of listing everything.
 */
export function deriveTags(text: string, max = 4): string[] {
  const hay = foldText(text);
  const scored: { slug: string; score: number }[] = [];
  for (const t of TAGS) {
    let score = 0;
    for (const m of t.match) {
      const hits = hay.split(m).length - 1;
      if (hits > 0) score += hits + m.length / 10;
    }
    if (score >= 2) scored.push({ slug: t.slug, score });
  }
  scored.sort((a, b) => b.score - a.score);
  return scored.slice(0, max).map((s) => s.slug);
}

/**
 * Single source of truth for a post's tags: curated tags win, and when a post
 * has none yet they are derived from its text. Every surface (article page,
 * tag archives, RSS, MCP/API) must go through this so the page and the data
 * layer can never disagree.
 */
export function resolveTags(post: {
  tags?: readonly string[] | null;
  title_tr?: string | null;
  title_en?: string | null;
  excerpt_tr?: string | null;
  excerpt_en?: string | null;
  content_tr?: string | null;
  content_en?: string | null;
}): string[] {
  const curated = sanitizeTags(post.tags);
  if (curated.length) return curated;
  return deriveTags(
    [post.title_tr, post.title_en, post.excerpt_tr, post.excerpt_en, post.content_tr, post.content_en]
      .filter(Boolean)
      .join(" "),
  );
}
